<script setup>
import _ from 'lodash'
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import Badge from '../Badge.vue'
import IconBase from '../icon/IconBase.vue'
import IconPlus from '../icon/IconPlus.vue'
import IconCross from '../icon/IconCross.vue'
import IconSearch from '../icon/IconSearch.vue'
import IconCheckmark from '../icon/IconCheckmark.vue'
import IconBin from '../icon/IconBin.vue'
import { sortTagsUtil } from "../../utils"
import StreamService from '@/services/StreamService'
import ModalManageTags from '../modals/ModalManageTags.vue'
import FormButton from '../Atoms/FormButton.vue'

const { notify } = useNotification()
const store = useStore()

const props = defineProps({
	videoId: { type: String, default: '' },
	tagIds: { type: Array, default: () => [] },
	liveParent: { type: Object },
	streamParent: { type: Object }
})

const COLORS = ['color-1', 'color-2', 'color-3', 'color-4', 'color-5', 'color-6', 'color-7', 'color-8', 'color-9']

const rootEl = ref(null)
const triggerEl = ref(null)
const searchInput = ref(null)
const manageTagsModal = ref(null)

const open = ref(false)
// Opens upward when there isn't enough room below the trigger (e.g. near the
// bottom of a modal), so the picker never spills outside its container.
const dropUp = ref(false)
const query = ref('')
const busy = ref(false)

// Frozen A–Z order captured when the picker opens, so renaming/recoloring a tag
// doesn't make it hop to a new position mid-edit. Re-sorted fresh on next open.
const frozenOrder = ref([])

// inline edit state
const editingId = ref(null)
const editTitle = ref('')
const editColor = ref('color-1')

// inline delete-from-account confirm
const confirmDeleteId = ref(null)

// color used for the next "create" action (user can cycle it)
const newColor = ref('color-1')

const generalTags = computed(() => store.getters['Ui/generalTags'] || [])

// Optimistic copy of the assigned tag ids so chips update instantly on toggle,
// while the API + folder refresh happen in the background. Reconciled whenever
// the parent passes fresh tagIds.
const localTagIds = ref([...(props.tagIds || [])])
watch(() => props.tagIds, (v) => { localTagIds.value = [...(v || [])] }, { deep: true })

// Tags currently assigned to this video (shown as chips)
const tags = computed(() => {
	const fileTags = generalTags.value.filter(tag => localTagIds.value.includes(tag.id))
	return sortTagsUtil(_.cloneDeep(fileTags), 'A to Z')
})

// All tags, filtered by the search box (used in the picker list)
const filteredTags = computed(() => {
	const q = query.value.trim().toLowerCase()
	const list = _.cloneDeep(q
		? generalTags.value.filter(tag => tag?.title?.toLowerCase().includes(q))
		: generalTags.value)
	// While open, keep the order frozen from open-time (newly created tags fall to
	// the bottom). When closed, fall back to a plain A–Z sort.
	if (frozenOrder.value.length) {
		const idx = (id) => {
			const i = frozenOrder.value.indexOf(id)
			return i === -1 ? Number.MAX_SAFE_INTEGER : i
		}
		return list.sort((a, b) => (idx(a.id) - idx(b.id)) || a.title.localeCompare(b.title))
	}
	return sortTagsUtil(list, 'A to Z')
})

const trimmedQuery = computed(() => query.value.trim())
const canCreate = computed(() => {
	const q = trimmedQuery.value.toLowerCase()
	if (!q) return false
	return !generalTags.value.some(tag => tag?.title?.toLowerCase() === q)
})

const isAssigned = (tag) => localTagIds.value.includes(tag.id)

/* ---------- open / close ---------- */
const togglePanel = () => {
	open.value ? closePanel() : openPanel()
}
const onDocClick = (e) => {
	if (rootEl.value && !rootEl.value.contains(e.target)) closePanel()
}
const openPanel = async () => {
	open.value = true
	// Snapshot the current A–Z order so edits don't reshuffle the list live.
	frozenOrder.value = sortTagsUtil(_.cloneDeep(generalTags.value), 'A to Z').map(t => t.id)
	// Flip upward if there's more room above than below the trigger.
	const r = triggerEl.value?.getBoundingClientRect?.()
	if (r) {
		const spaceBelow = window.innerHeight - r.bottom
		dropUp.value = spaceBelow < 340 && r.top > spaceBelow
	}
	// Register the outside-click listener only after this opening click has
	// finished, otherwise the same click would immediately close the panel.
	setTimeout(() => document.addEventListener('mousedown', onDocClick), 0)
	await nextTick()
	searchInput.value?.focus()
}
const closePanel = () => {
	open.value = false
	query.value = ''
	editingId.value = null
	confirmDeleteId.value = null
	frozenOrder.value = []
	document.removeEventListener('mousedown', onDocClick)
}
const onKeydown = (e) => {
	if (e.key === 'Escape' && open.value) {
		if (editingId.value) cancelEdit()
		else if (confirmDeleteId.value) confirmDeleteId.value = null
		else closePanel()
	}
}
onMounted(() => {
	if (!generalTags.value.length) store.dispatch('Ui/getAllTags')
	window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
	document.removeEventListener('mousedown', onDocClick)
	window.removeEventListener('keydown', onKeydown)
})

/* ---------- assign / unassign ---------- */
const persistVideoTags = async (updatedTagIds) => {
	await store.dispatch('Ui/updateTagsOfVideoFile', {
		videoId: props.videoId,
		updatedTagIds,
		parent: props.liveParent,
		parentId: props.streamParent?.liveParent
	})
	await store.dispatch('Streams/getFilesInVODFolder')
}

const toggleAssign = async (tag) => {
	const ids = _.cloneDeep(localTagIds.value || [])
	const idx = ids.indexOf(tag.id)
	if (idx >= 0) ids.splice(idx, 1)
	else ids.push(tag.id)
	localTagIds.value = ids // optimistic — chips update instantly
	await persistVideoTags(ids)
}

const unassign = async (tag) => {
	const ids = _.cloneDeep(localTagIds.value || []).filter(id => id !== tag.id)
	localTagIds.value = ids // optimistic
	await persistVideoTags(ids)
}

/* ---------- create ---------- */
const cycleNewColor = () => {
	const i = COLORS.indexOf(newColor.value)
	newColor.value = COLORS[(i + 1) % COLORS.length]
}

const createAndAssign = async () => {
	const title = trimmedQuery.value
	if (!title || busy.value) return
	busy.value = true
	try {
		await store.dispatch('Ui/addTag', { title, color: newColor.value })
		await store.dispatch('Ui/getAllTags')
		const created = generalTags.value.find(t => t.title.toLowerCase() === title.toLowerCase())
		if (created) {
			// Keep the new tag pinned to the bottom of the frozen list order.
			if (!frozenOrder.value.includes(created.id)) frozenOrder.value.push(created.id)
			const ids = _.uniq([...(localTagIds.value || []), created.id])
			localTagIds.value = ids // optimistic
			await persistVideoTags(ids)
		}
		notify({ group: 'success', text: `Tag “${title}” created` })
		query.value = ''
		cycleNewColor()
		await nextTick()
		searchInput.value?.focus()
	} catch (err) {
		notify({ group: 'error', text: "Couldn't create tag" })
	} finally {
		busy.value = false
	}
}

const onEnter = () => {
	if (canCreate.value) createAndAssign()
}

/* ---------- edit (rename + recolor) ---------- */
const startEdit = (tag) => {
	confirmDeleteId.value = null
	editingId.value = tag.id
	editTitle.value = tag.title
	editColor.value = tag.color
}
const cancelEdit = () => {
	editingId.value = null
}
const saveEdit = async (tag) => {
	const title = editTitle.value.trim()
	if (!title) {
		notify({ group: 'error', text: 'Tag name cannot be empty' })
		return
	}
	const dup = generalTags.value.some(t => t.id !== tag.id && t.title.toLowerCase() === title.toLowerCase())
	if (dup) {
		notify({ group: 'error', text: 'A tag with this name already exists' })
		return
	}
	if (title === tag.title && editColor.value === tag.color) {
		cancelEdit()
		return
	}
	busy.value = true
	try {
		await StreamService.updateTagById(tag.id, { title, color: editColor.value })
		await store.dispatch('Ui/getAllTags')
		notify({ group: 'success', text: 'Tag updated' })
		cancelEdit()
	} catch (err) {
		notify({ group: 'error', text: "Couldn't update tag" })
	} finally {
		busy.value = false
	}
}

/* ---------- delete from account ---------- */
const askDelete = (tag) => {
	editingId.value = null
	confirmDeleteId.value = tag.id
}
const removeTagGlobally = async (tag) => {
	busy.value = true
	try {
		await store.dispatch('Ui/deleteTag', tag)
		await store.dispatch('Streams/getFilesInVODFolder')
		notify({ group: 'success', text: `Tag “${tag.title}” deleted` })
		confirmDeleteId.value = null
	} catch (err) {
		notify({ group: 'error', text: "Couldn't delete tag" })
	} finally {
		busy.value = false
	}
}

// Kept for backwards compatibility with callers that opened the legacy modal.
const showManageTagsModal = () => manageTagsModal.value?.toggleModal()
defineExpose({ showManageTagsModal })
</script>

<template>
	<div ref="rootEl" class="relative inline-flex items-center gap-1.5 flex-wrap">
		<!-- Assigned tag chips -->
		<Badge v-for="tag in tags" :key="tag.id" tag :type="tag.color" size="sm"
			class="group items-center gap-1 !pr-1 !pl-2.5">
			<span class="truncate max-w-[140px]">{{ tag.title }}</span>
			<FormButton @click.stop="unassign(tag)"
				custom-classes="grid place-items-center size-3.5 rounded-full opacity-60 hover:opacity-100 hover:bg-black/25 transition"
				title="Remove tag">
				<icon-base width="8" height="8" fill="transparent">
					<icon-cross />
				</icon-base>
			</FormButton>
		</Badge>

		<!-- Trigger -->
		<FormButton ref="triggerEl" @click.stop="togglePanel"
			:custom-classes="'inline-flex items-center gap-1 h-6 px-2 rounded-full text-xs font-medium bg-surface-3 text-surface-8 hover:text-white hover:bg-surface-4 transition select-none' + (open ? ' !bg-surface-4 text-white' : '')"
			title="Add tag">
			<icon-base width="11" height="11" fill="transparent">
				<icon-plus />
			</icon-base>
			Tag
		</FormButton>

		<!-- Picker popover -->
		<div v-if="open"
			class="absolute z-30 left-0 w-64 rounded-lg bg-surface-2 ring-1 ring-surface-3 shadow-dropdown overflow-hidden"
			:class="dropUp ? 'bottom-full mb-1.5 origin-bottom-left' : 'top-full mt-1.5 origin-top-left'"
			@click.stop>
			<!-- Search / create field -->
			<div class="p-2 border-b border-surface-3">
				<div class="relative flex items-center">
					<icon-base class="absolute left-2.5 text-surface-8 pointer-events-none" width="14" height="14"
						fill="none">
						<icon-search />
					</icon-base>
					<input ref="searchInput" v-model="query" @keydown.enter.prevent="onEnter"
						type="text" placeholder="Search or create a tag…"
						class="w-full h-8 pl-8 pr-7 rounded-md bg-surface-3 focus:bg-surface-4 text-sm text-surface-text-1 placeholder-surface-8 focus:outline-none transition" />
					<FormButton v-if="query" @click="query = ''"
						custom-classes="absolute right-2 text-surface-8 hover:text-white" title="Clear">
						<icon-base width="12" height="12" fill="transparent">
							<icon-cross />
						</icon-base>
					</FormButton>
				</div>
			</div>

			<!-- Tag list -->
			<div class="max-h-56 overflow-y-auto p-1">
				<template v-for="tag in filteredTags" :key="tag.id">
					<!-- Edit mode -->
					<div v-if="editingId === tag.id" class="rounded-md bg-surface-3 p-2 my-0.5">
						<div class="flex items-center gap-2">
							<input v-model="editTitle" @keydown.enter.prevent="saveEdit(tag)"
								@keydown.esc.prevent="cancelEdit" type="text"
								class="flex-1 min-w-0 h-7 px-2 rounded bg-surface-4 text-sm text-surface-text-1 focus:outline-none" />
							<FormButton @click="saveEdit(tag)"
								custom-classes="grid place-items-center size-7 rounded text-main hover:bg-surface-4" title="Save">
								<icon-base width="14" height="14" fill="none">
									<icon-checkmark />
								</icon-base>
							</FormButton>
							<FormButton @click="cancelEdit"
								custom-classes="grid place-items-center size-7 rounded text-surface-8 hover:text-white hover:bg-surface-4"
								title="Cancel">
								<icon-base width="12" height="12" fill="transparent">
									<icon-cross />
								</icon-base>
							</FormButton>
						</div>
						<div class="flex items-center gap-1.5 mt-2">
							<FormButton v-for="c in COLORS" :key="c" @click="editColor = c"
								:custom-classes="'tag-color !size-4 rounded-full transition ' + c + ' ' + (editColor === c ? 'ring-2 ring-offset-2 ring-offset-surface-3 ring-white' : 'hover:ring-2 hover:ring-surface-6')" />
						</div>
					</div>

					<!-- Delete confirm -->
					<div v-else-if="confirmDeleteId === tag.id"
						class="flex items-center gap-2 rounded-md px-2 h-9 my-0.5 bg-error-transparent">
						<span class="text-xs text-surface-text-1 truncate flex-1">Delete “{{ tag.title }}” everywhere?</span>
						<FormButton @click="removeTagGlobally(tag)"
							custom-classes="h-6 px-2 rounded text-xs font-medium bg-error text-white hover:opacity-90">Delete</FormButton>
						<FormButton @click="confirmDeleteId = null"
							custom-classes="h-6 px-2 rounded text-xs font-medium bg-surface-4 text-surface-text-1 hover:text-white">Keep</FormButton>
					</div>

					<!-- Normal row -->
					<div v-else class="group flex items-center rounded-md h-9 pl-2 pr-1 my-0.5 hover:bg-surface-3 transition">
						<FormButton @click="toggleAssign(tag)"
							custom-classes="flex items-center gap-2 flex-1 min-w-0 text-left h-full">
							<span class="tag-color !size-3 rounded-full shrink-0" :class="tag.color" />
							<span class="truncate text-sm text-surface-text-1">{{ tag.title }}</span>
							<icon-base v-if="isAssigned(tag)" class="ml-auto text-main shrink-0" width="15" height="15"
								fill="none">
								<icon-checkmark />
							</icon-base>
						</FormButton>
						<div class="flex items-center gap-0.5 pl-1 opacity-0 group-hover:opacity-100 transition">
							<FormButton @click.stop="startEdit(tag)"
								custom-classes="grid place-items-center size-6 rounded text-surface-8 hover:text-white hover:bg-surface-4"
								title="Edit tag">
								<icon-base width="13" height="13" fill="transparent">
									<path
										d="M11.013 1.427a1.75 1.75 0 0 1 2.474 0l1.086 1.086a1.75 1.75 0 0 1 0 2.474l-8.61 8.61c-.21.21-.47.364-.756.445l-3.251.93a.75.75 0 0 1-.927-.928l.929-3.25c.081-.286.235-.547.445-.758l8.61-8.61Zm.176 1.412-.882.882 2.343 2.343.883-.882a.25.25 0 0 0 0-.354l-1.99-1.99a.25.25 0 0 0-.353 0Z"
										fill="currentColor" />
								</icon-base>
							</FormButton>
							<FormButton @click.stop="askDelete(tag)"
								custom-classes="grid place-items-center size-6 rounded text-surface-8 hover:text-error-light hover:bg-surface-4"
								title="Delete tag">
								<icon-base width="13" height="13" fill="transparent">
									<icon-bin />
								</icon-base>
							</FormButton>
						</div>
					</div>
				</template>

				<!-- Empty state -->
				<div v-if="filteredTags.length === 0 && !canCreate"
					class="px-2 py-4 text-center text-xs text-surface-8">
					No tags yet
				</div>
			</div>

			<!-- Create row -->
			<div v-if="canCreate" class="p-1 border-t border-surface-3">
				<FormButton @click="createAndAssign" :disabled="busy"
					custom-classes="flex items-center gap-2 w-full h-9 px-2 rounded-md hover:bg-surface-3 text-left transition disabled:opacity-60">
					<icon-base class="text-main shrink-0" width="13" height="13" fill="transparent">
						<icon-plus />
					</icon-base>
					<span class="text-sm text-surface-text-1 truncate">Create “{{ trimmedQuery }}”</span>
					<span class="tag-color !size-4 rounded-full shrink-0 ml-auto ring-1 ring-surface-6 hover:ring-2"
						:class="newColor" @click.stop="cycleNewColor" title="Change color" />
				</FormButton>
			</div>
		</div>

		<ModalManageTags ref="manageTagsModal" />
	</div>
</template>

<style scoped>
</style>
